import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  AcuityLevel,
  AcuityLevelUpdateDialogData,
  AcuityLevelUpdateDialogResult,
  ManualAcuityUpdateResponse,
} from 'src/app/models/acuity.model';
import { AcuityService } from 'src/app/services/acuity/acuity.service';
import { JourneyJobsService } from 'src/app/services/journey/journey-jobs.service';
import { SnackService } from 'src/app/services/snack.service';

type AcuityDirection = 'escalation' | 'deescalation';

const LEVEL_ORDER: Record<AcuityLevel, number> = { low: 0, medium: 1, high: 2 };

@Component({
  selector: 'app-acuity-level-update-dialog',
  templateUrl: './acuity-level-update-dialog.component.html',
  styleUrls: ['./acuity-level-update-dialog.component.scss'],
})
export class AcuityLevelUpdateDialogComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  triggers: string[] = [];
  direction: AcuityDirection;
  isLoading = true;
  isSaving = false;
  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AcuityLevelUpdateDialogData,
    private dialogRef: MatDialogRef<AcuityLevelUpdateDialogComponent, AcuityLevelUpdateDialogResult>,
    private fb: UntypedFormBuilder,
    private acuityService: AcuityService,
    private journeyJobsService: JourneyJobsService,
    private snackBarService: SnackService
  ) {
    this.form = this.fb.group({
      checks: this.fb.array([] as UntypedFormControl[]),
      comments: this.fb.control(''),
    });
    this.direction =
      LEVEL_ORDER[this.data.requestedLevel] > LEVEL_ORDER[this.data.currentLevel] ? 'escalation' : 'deescalation';
  }

  get checksArray(): UntypedFormArray {
    return this.form.get('checks') as UntypedFormArray;
  }

  ngOnInit(): void {
    this.acuityService
      .getManualTriggers()
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (doc) => {
          this.triggers = (this.direction === 'escalation' ? doc?.escalation : doc?.deescalation) ?? [];
          this.triggers.forEach(() => this.checksArray.push(this.fb.control(false)));
        },
        error: () => {
          this.triggers = [];
          this.snackBarService.genericSnackBar('Failed to load triggers. Please try again.', ['error-snackbar'], 3000);
        },
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get canSubmit(): boolean {
    const anyChecked = this.checksArray.controls.some((c) => c.value === true);
    const commentsValue = (this.form.get('comments')?.value ?? '').trim();
    return anyChecked || commentsValue.length > 0;
  }

  onCancel(): void {
    if (this.isSaving) {
      return;
    }
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (!this.canSubmit || this.isSaving) {
      return;
    }
    const selectedTriggers = this.triggers.filter((_, i) => this.checksArray.at(i)?.value === true);
    const comments = (this.form.get('comments')?.value ?? '').trim();

    this.isSaving = true;
    this.form.disable({ emitEvent: false });

    this.acuityService
      .saveManualAcuityRequest(this.data.patientId, {
        requestedLevel: this.data.requestedLevel,
        selectedTriggers,
        comments: comments.length > 0 ? comments : undefined,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          if (response.applied) {
            this.journeyJobsService.triggerMidMonthUpdate(this.data.patientId);
          }
          this.dialogRef.close({ response });
        },
        error: () => {
          this.snackBarService.genericSnackBar(
            'Failed to submit acuity update request. Please try again.',
            ['error-snackbar'],
            5000
          );
          this.isSaving = false;
          this.form.enable({ emitEvent: false });
        },
      });
  }
}
